/**
 * InteractiveQuestionCard — renders an agent `question` set as a stepper the
 * founder answers by picking options (ids), not by typing prose. Mirrors the
 * Claude-web MCQ card: one question per panel — a titled question, a numbered
 * list of options (radios for single_select, checkboxes for multi_select), an
 * optional "Something else" free-text row (allow_custom), and Skip/Next/Send
 * controls. Answering or skipping a question reveals the next; the last one
 * submits the whole set.
 *
 * The whole set is submitted at once. `onSubmit` receives the answer body with
 * `selected_option_ids` (never labels) + optional `custom_text`. The chat box
 * stays usable — a founder can ignore this and just type (handled by the host).
 *
 * Skip is a real answer, not an omission: a declined question goes out as
 * `skipped: true` with no selection, which the backend records as `skipped` and
 * renders to the planner as "— (declined)". Sending an empty selection instead
 * is rejected, and leaves the agent free to re-ask what it was never told.
 *
 * See: Interactive questions — frontend integration guide;
 * `docs/Future-Work/Skipping-A-Structured-Question.md` (ideoz-ai-api).
 */

import React, { useEffect, useState } from 'react';
import {
  ArrowRight,
  Check,
  ChevronLeft,
  ChevronRight,
  CornerDownLeft,
  Pencil,
  X,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from '../atoms/button';
import type { Question, QuestionAnswerBody, QuestionSetPayload } from '@ideoz/core';

interface InteractiveQuestionCardProps {
  questionSet: QuestionSetPayload;
  onSubmit: (answer: QuestionAnswerBody) => void;
  onDismiss?: () => void;
  isSubmitting?: boolean;
  /**
   * Why the last submit was rejected. The card stays mounted on a rejection so
   * the founder can fix it here; this renders that reason above the questions.
   */
  error?: string | null;
  /** Called on the first edit after a rejection, to clear `error`. */
  onErrorDismiss?: () => void;
  className?: string;
}

interface AnswerState {
  selected: string[];
  custom: string;
  /** The founder pressed Skip on this question — declined, not merely blank. */
  skipped: boolean;
}

const initialState = (questions: Question[]): Record<string, AnswerState> => {
  const s: Record<string, AnswerState> = {};
  for (const q of questions) s[q.question_id] = { selected: [], custom: '', skipped: false };
  return s;
};

/** A free_text question's whole answer is the typed prose; a select question
 *  only accepts prose when allow_custom is set. */
const acceptsCustomText = (q: Question): boolean => q.kind === 'free_text' || !!q.allow_custom;

/** A question is satisfied when it's optional, or its min selection is met. */
const isQuestionSatisfied = (q: Question, a: AnswerState): boolean => {
  if (!q.required) return true;
  const customCounts = acceptsCustomText(q) && a.custom.trim() ? 1 : 0;
  return a.selected.length + customCounts >= (q.min_selections ?? 1);
};

export function InteractiveQuestionCard({
  questionSet,
  onSubmit,
  onDismiss,
  isSubmitting = false,
  error = null,
  onErrorDismiss,
  className,
}: InteractiveQuestionCardProps): JSX.Element {
  const { questions } = questionSet;
  const [answers, setAnswers] = useState<Record<string, AnswerState>>(() =>
    initialState(questions)
  );
  // Stepper position — one question is shown at a time; answering or skipping
  // advances to the next, and the last question submits the whole set.
  const [index, setIndex] = useState(0);

  // A new set (or a re-ask) resets the stepper and answers.
  useEffect(() => {
    setAnswers(initialState(questions));
    setIndex(0);
  }, [questionSet.question_set_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const q = questions[index];
  const a = answers[q.question_id];
  const isLast = index === questions.length - 1;
  const total = questions.length;
  const satisfied = isQuestionSatisfied(q, a);

  const buildAnswer = (source: Record<string, AnswerState>): QuestionAnswerBody => ({
    question_set_id: questionSet.question_set_id,
    answers: questions.map((question) => {
      const state = source[question.question_id];
      const custom = acceptsCustomText(question) ? state.custom.trim() : '';
      // A question the founder gave nothing to is declined, whether they said so
      // with Skip or reached Send having paged past it. Either way it goes out as
      // a decline — an empty selection is not a thing the backend accepts, and
      // the decline is what stops the agent re-asking.
      if (state.skipped || (state.selected.length === 0 && !custom)) {
        return {
          question_id: question.question_id,
          selected_option_ids: [],
          skipped: true,
        };
      }
      return {
        question_id: question.question_id,
        selected_option_ids: state.selected,
        ...(custom ? { custom_text: custom } : {}),
      };
    }),
  });

  // Advance to the next question, or submit the whole set on the last one.
  const advance = (source: Record<string, AnswerState>) => {
    if (isSubmitting) return;
    if (isLast) onSubmit(buildAnswer(source));
    else setIndex((i) => i + 1);
  };

  // Header pagination: free navigation between questions, no answer required.
  const goPrev = () => !isSubmitting && setIndex((i) => Math.max(0, i - 1));
  const goNext = () => !isSubmitting && setIndex((i) => Math.min(total - 1, i + 1));

  // single_select: clicking an option records it and advances/submits (⏎).
  // multi_select / free_text: an explicit arrow button advances instead.
  const autoAdvanceOnPick = q.kind === 'single_select';
  const showArrow = q.kind === 'multi_select' || q.kind === 'free_text';

  const next = () => {
    if (isSubmitting || !satisfied) return;
    advance(answers);
  };

  // Skip = decline this question and move on. The decline is recorded (it goes
  // out as `skipped: true`, clearing anything typed or picked — the backend
  // rejects a body that both declines and answers) and the set still submits.
  const skip = () => {
    if (isSubmitting) return;
    const nextAnswers = {
      ...answers,
      [q.question_id]: { selected: [], custom: '', skipped: true },
    };
    setAnswers(nextAnswers);
    onErrorDismiss?.();
    advance(nextAnswers);
  };

  const hasCurrentAnswer =
    a.selected.length > 0 || (acceptsCustomText(q) && a.custom.trim().length > 0);

  const toggleOption = (optionId: string) => {
    onErrorDismiss?.();
    const cur = answers[q.question_id];
    let selected: string[];
    if (q.kind === 'single_select') {
      selected = [optionId];
    } else {
      const has = cur.selected.includes(optionId);
      if (has) selected = cur.selected.filter((id) => id !== optionId);
      else {
        if (cur.selected.length >= (q.max_selections ?? cur.selected.length + 1)) return;
        selected = [...cur.selected, optionId];
      }
    }
    // Answering undoes a previous decline on the same question.
    const nextAnswers = {
      ...answers,
      [q.question_id]: { selected, custom: cur.custom, skipped: false },
    };
    setAnswers(nextAnswers);
    if (autoAdvanceOnPick) advance(nextAnswers);
  };

  // A declined question keeps saying so when the founder pages back to it, so
  // the state is legible before they hit Send.
  const declined = a.skipped;
  const selectedLabel = declined
    ? 'Skipped'
    : q.kind === 'multi_select' && a.selected.length > 0
      ? `${a.selected.length} selected`
      : '';
  const hasCustomRow = acceptsCustomText(q);
  // Clean, Claude-style layout: when a question has a "type something" row
  // (free_text or a single_select's custom box), the Skip/arrow controls sit on
  // that same row — no empty footer band. multi_select and plain single_select
  // keep a compact footer (multi also shows its running "N selected" count).
  const inlineControls = hasCustomRow && q.kind !== 'multi_select';
  const showStandaloneFooter = !inlineControls;

  const setCustom = (value: string) => {
    onErrorDismiss?.();
    setAnswers((prev) => ({
      ...prev,
      [q.question_id]: { ...prev[q.question_id], custom: value, skipped: false },
    }));
  };

  return (
    <div
      className={cn(
        'rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-900',
        className
      )}
      data-testid="interactive-question-card"
    >
      {/* Header — question + pager + dismiss */}
      <div className="flex items-start justify-between gap-3 px-5 pt-4 pb-3">
        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">{q.question}</h3>
        <div className="flex flex-shrink-0 items-center gap-1.5">
          {total > 1 && (
            <div className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400">
              <button
                type="button"
                onClick={goPrev}
                disabled={index === 0 || isSubmitting}
                aria-label="Previous question"
                className="rounded p-0.5 text-gray-400 hover:text-gray-700 disabled:opacity-30 dark:hover:text-gray-200"
              >
                <ChevronLeft size={18} />
              </button>
              <span className="tabular-nums">
                {index + 1} of {total}
              </span>
              <button
                type="button"
                onClick={goNext}
                disabled={isLast || isSubmitting}
                title="Next question"
                aria-label="Next question"
                className="rounded p-0.5 text-gray-400 hover:text-gray-700 disabled:opacity-30 dark:hover:text-gray-200"
              >
                <ChevronRight size={18} />
              </button>
            </div>
          )}
          {onDismiss && (
            <button
              type="button"
              onClick={onDismiss}
              aria-label="Dismiss"
              className="rounded p-0.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <X size={16} />
            </button>
          )}
        </div>
      </div>

      {/* A rejected submit — the set was not taken, so the founder fixes it
          here rather than losing the form. */}
      {error && (
        <div
          role="alert"
          className="mx-5 mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-300"
          data-testid="question-error"
        >
          {error}
        </div>
      )}

      {/* Options + "type something" row — full-width rows split by hairlines. */}
      <div className="divide-y divide-gray-100 dark:divide-gray-800">
        {q.kind !== 'free_text' &&
          q.options.map((opt, i) => {
            const active = a.selected.includes(opt.id);
            return (
              <button
                key={opt.id}
                type="button"
                disabled={isSubmitting}
                onClick={() => toggleOption(opt.id)}
                data-testid={`question-option-${opt.id}`}
                className={cn(
                  'group flex w-full items-center gap-3 px-5 py-3 text-left transition-colors',
                  active
                    ? 'bg-gray-50 dark:bg-gray-800/60'
                    : 'hover:bg-gray-50 dark:hover:bg-gray-800/40'
                )}
              >
                {q.kind === 'multi_select' ? (
                  <span
                    className={cn(
                      'flex h-5 w-5 flex-shrink-0 items-center justify-center rounded border transition-colors',
                      active
                        ? 'border-primary-600 bg-primary-600 text-white'
                        : 'border-gray-300 dark:border-gray-600'
                    )}
                  >
                    {active && <Check size={13} />}
                  </span>
                ) : (
                  <span
                    className={cn(
                      'flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md text-sm',
                      active
                        ? 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200'
                        : 'bg-gray-100 text-gray-400 dark:bg-gray-800 dark:text-gray-500'
                    )}
                  >
                    {i + 1}
                  </span>
                )}
                <span className="min-w-0 flex-1 text-[15px] text-gray-800 dark:text-gray-200">
                  {opt.label}
                </span>
                {autoAdvanceOnPick && (
                  <CornerDownLeft
                    size={16}
                    className="flex-shrink-0 text-gray-300 opacity-0 group-hover:opacity-100"
                  />
                )}
              </button>
            );
          })}

        {/* Free-text: the question itself is prose. allow_custom: an extra
            "Something else" row. When the controls belong on this row
            (free_text / single_select-with-custom) they render inline here. */}
        {hasCustomRow && (
          <div className="flex items-center gap-3 px-5 py-2.5">
            <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md bg-gray-100 text-gray-400 dark:bg-gray-800">
              <Pencil size={13} />
            </span>
            <input
              type="text"
              value={a.custom}
              disabled={isSubmitting}
              placeholder={q.custom_label || 'Something else'}
              onChange={(e) => setCustom(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && satisfied) next();
              }}
              className="min-w-0 flex-1 bg-transparent text-[15px] text-gray-800 outline-none placeholder:text-gray-400 dark:text-gray-100"
              data-testid={`question-custom-${q.question_id}`}
            />
            {inlineControls && (
              <div className="flex flex-shrink-0 items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="xs"
                  onClick={skip}
                  disabled={isSubmitting}
                  aria-pressed={declined}
                  data-testid="question-skip"
                  className={cn(declined && 'border-gray-400 text-gray-500 dark:border-gray-500')}
                >
                  {declined ? 'Skipped' : 'Skip'}
                </Button>
                {showArrow && (
                  <Button
                    variant="primary"
                    size="xs"
                    onClick={next}
                    disabled={isSubmitting || !satisfied || !hasCurrentAnswer}
                    aria-label={isLast ? 'Send' : 'Next question'}
                    title={isLast ? 'Send' : 'Next question'}
                    data-testid={isLast ? 'question-send' : 'question-next'}
                    className="flex h-9 w-9 items-center justify-center rounded-lg bg-gray-900 text-white transition-opacity hover:opacity-90 disabled:opacity-30 dark:bg-gray-100 dark:text-gray-900"
                  >
                    <ArrowRight size={18} />
                  </Button>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Compact footer — multi_select (running count + Skip + arrow) and plain
          single_select (Skip only). Sits flush under the last row. */}
      {showStandaloneFooter && (
        <div className="flex items-center justify-between gap-2 border-t border-gray-100 px-5 py-2.5 dark:border-gray-800">
          <span className="text-sm text-gray-500 dark:text-gray-400">{selectedLabel}</span>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="xs"
              onClick={skip}
              disabled={isSubmitting}
              aria-pressed={declined}
              data-testid="question-skip"
              className={cn(declined && 'border-gray-400 text-gray-500 dark:border-gray-500')}
            >
              {declined ? 'Skipped' : 'Skip'}
            </Button>
            {showArrow && (
              <Button
                variant="primary"
                size="xs"
                onClick={next}
                disabled={isSubmitting || !satisfied || !hasCurrentAnswer}
                aria-label={isLast ? 'Send' : 'Next question'}
                title={isLast ? 'Send' : 'Next question'}
                data-testid={isLast ? 'question-send' : 'question-next'}
              >
                <ArrowRight size={18} />
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default InteractiveQuestionCard;
